/**
 * /site-map/:propertyId — the site map editor, full screen.
 *
 * A real route rather than a modal so it opens the same from anywhere: the
 * commercial account's site card, the office job page, a yard's card under
 * Customers. Loads the marks and hands them to components/SiteMapEditor.
 */
import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import SiteMapEditor, { SiteMarksPayload } from '../components/SiteMapEditor';

export default function SiteMapPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const id = Number(propertyId);
  const { data, isLoading, error } = useQuery(['site-marks', id],
    () => api.get(`/properties/${id}/site-marks`).then(r => r.data as SiteMarksPayload),
    { enabled: Number.isFinite(id) && id > 0, retry: false });

  // Back to wherever the map was opened from; a link opened fresh (no history) lands on the customer.
  const close = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate(data?.property?.customer_id ? `/customers?profile=${data.property.customer_id}` : '/customers');
  };

  if (!Number.isFinite(id) || id <= 0) return <p className="text-sm text-red-600">That is not a property. <Link to="/customers" className="underline">Customers</Link></p>;
  if (isLoading) return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  if (error || !data) {
    const status = (error as any)?.response?.status;
    return (
      <div className="py-12 text-center space-y-3">
        <p className="text-red-600 font-semibold">
          {status === 403 ? 'This login cannot open the site map.' : status === 404 ? 'That property is gone.' : 'Could not load the site map.'}
        </p>
        <button onClick={() => navigate(-1)} className="text-brand-600 font-medium">← Back</button>
      </div>
    );
  }
  return <SiteMapEditor data={data} onClose={close} />;
}
